import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

const TOOLTIP_W = 300;
const GAP = 14;
const PADDING = 8;

/**
 * TutorialTooltip — a single step in the onboarding tour.
 *
 * Spotlight technique: renders 4 fixed-position backdrop panels that leave a
 * transparent cutout at the target's position. Works regardless of the target's
 * stacking context (sidebar, fixed headers, modals) because everything is
 * rendered in a document.body portal.
 *
 * Step fields:
 *   title                — heading text
 *   body                 — paragraph text
 *   targetSelector       — CSS selector for the element to highlight
 *   mobileTargetSelector — CSS selector used instead on viewports < 768px
 *   position             — 'below' | 'above' | 'right' | 'center' (default 'below')
 *   onActivate           — optional side-effect called when the step becomes active
 *                          (e.g. open a modal). Position is re-computed after 300ms
 *                          to allow the DOM to update.
 */
export default function TutorialTooltip({ step, stepIndex, total, onNext, onSkip }) {
  const [layout, setLayout] = useState(null);
  const tooltipRef = useRef(null);
  const retryTimerRef = useRef(null);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const computeLayout = useCallback((s) => {
    if (!s) return;

    const selector = (isMobile && s.mobileTargetSelector) || s.targetSelector;
    const target   = selector ? document.querySelector(selector) : null;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    if (!target) {
      setLayout({ centered: true, vw, vh });
      return;
    }

    const rect = target.getBoundingClientRect();

    // If the element is hidden (display:none returns zero rect), treat as centered
    if (rect.width === 0 && rect.height === 0) {
      setLayout({ centered: true, vw, vh });
      return;
    }

    // Spotlight box with padding
    const spot = {
      top:    Math.max(0, rect.top    - PADDING),
      left:   Math.max(0, rect.left   - PADDING),
      right:  Math.min(vw, rect.right  + PADDING),
      bottom: Math.min(vh, rect.bottom + PADDING),
    };
    spot.width  = spot.right  - spot.left;
    spot.height = spot.bottom - spot.top;

    let placement = s.position || 'below';
    const tooltipH = 180;

    if (placement === 'below' && spot.bottom + tooltipH + GAP > vh) placement = 'above';
    if (placement === 'above' && spot.top   - tooltipH - GAP < 0)  placement = 'below';

    let tooltipLeft = rect.left + rect.width / 2 - TOOLTIP_W / 2;
    tooltipLeft = Math.max(12, Math.min(tooltipLeft, vw - TOOLTIP_W - 12));

    let tooltipTop;
    if (placement === 'above') {
      tooltipTop = spot.top - GAP;
    } else if (placement === 'right') {
      tooltipTop = rect.top + rect.height / 2;
      tooltipLeft = Math.min(spot.right + GAP, vw - TOOLTIP_W - 12);
    } else {
      tooltipTop = spot.bottom + GAP;
    }

    setLayout({ spot, tooltipTop, tooltipLeft, placement, vw, vh });
  }, [isMobile]);

  // Fire onActivate side-effect when step changes
  useEffect(() => {
    step?.onActivate?.();
  }, [step]);

  // Compute layout whenever the step changes
  useEffect(() => {
    if (!step) return;

    computeLayout(step);

    // Scroll target into view (best-effort — may not exist yet if modal just opened)
    const selector = (isMobile && step.mobileTargetSelector) || step.targetSelector;
    document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Re-compute after 300ms to catch DOM changes from onActivate (e.g. modal opening)
    retryTimerRef.current = setTimeout(() => {
      computeLayout(step);
      // Scroll again once target is visible
      document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 300);

    const handleReflow = () => computeLayout(step);
    window.addEventListener('resize', handleReflow);
    window.addEventListener('scroll', handleReflow, true);

    return () => {
      clearTimeout(retryTimerRef.current);
      window.removeEventListener('resize', handleReflow);
      window.removeEventListener('scroll', handleReflow, true);
    };
  }, [step, computeLayout, isMobile]);

  if (!step || !layout) return null;

  const isLast = stepIndex === total - 1;
  const { spot, tooltipTop, tooltipLeft, placement, centered } = layout;

  // ---- Tooltip position style ----------------------------------------
  const tooltipStyle = centered
    ? { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: TOOLTIP_W }
    : placement === 'above'
    ? { position: 'fixed', top: tooltipTop, left: tooltipLeft, width: TOOLTIP_W, transform: 'translateY(-100%)' }
    : { position: 'fixed', top: tooltipTop, left: tooltipLeft, width: TOOLTIP_W };

  // ---- Backdrop panels (4-piece spotlight) ---------------------------
  const backdropBase = {
    position: 'fixed',
    background: 'rgba(0,0,0,0.58)',
    zIndex: 9000,
  };

  const panels = centered ? (
    <div style={{ ...backdropBase, inset: 0 }} onClick={onSkip} aria-hidden="true" />
  ) : (
    <>
      <div style={{ ...backdropBase, top: 0, left: 0, right: 0, height: spot.top }} onClick={onSkip} aria-hidden="true" />
      <div style={{ ...backdropBase, top: spot.bottom, left: 0, right: 0, bottom: 0 }} onClick={onSkip} aria-hidden="true" />
      <div style={{ ...backdropBase, top: spot.top, left: 0, width: spot.left, height: spot.height }} onClick={onSkip} aria-hidden="true" />
      <div style={{ ...backdropBase, top: spot.top, left: spot.right, right: 0, height: spot.height }} onClick={onSkip} aria-hidden="true" />
    </>
  );

  return createPortal(
    <>
      {panels}

      {/* Highlight ring positioned at target bounds — always above all backdrops */}
      {!centered && spot && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            top:    spot.top,
            left:   spot.left,
            width:  spot.width,
            height: spot.height,
            borderRadius: 10,
            outline: '3px solid var(--primary-dark, #FF85C0)',
            outlineOffset: 0,
            boxShadow: '0 0 0 4px rgba(255,133,192,0.3)',
            zIndex: 9100,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="tutorial-tooltip"
        style={tooltipStyle}
        role="dialog"
        aria-modal="false"
        aria-label={`Tutorial step ${stepIndex + 1} of ${total}: ${step.title}`}
      >
        {!centered && (
          <div className={`tutorial-arrow tutorial-arrow-${placement || 'below'}`} />
        )}

        <div className="tutorial-step-counter">
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={`tutorial-dot${i === stepIndex ? ' active' : ''}`} />
          ))}
        </div>

        <h4 className="tutorial-title">{step.title}</h4>
        <p className="tutorial-body">{step.body}</p>

        <div className="tutorial-actions">
          <button className="tutorial-btn-skip" onClick={onSkip}>
            Skip tour
          </button>
          <button className="tutorial-btn-next" onClick={onNext}>
            {isLast ? 'Got it 🎉' : 'Next →'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
